import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/EditCollectionSelectorPage.css";
import { getIdentifier } from "../utils/auth";

const DATE_LEEWAY_MS = 60 * 1000; // 1 minute

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—"); // dd/mm/yyyy

const normalize = (c) => {
  const created = c.created ?? c.createdAt ?? c.firstSavedAt ?? c.created_on ?? null;
  const updated = c.lastEdited ?? c.updatedAt ?? c.updated ?? c.modified_on ?? null;

  const isEdited =
    created && updated
      ? new Date(updated).getTime() - new Date(created).getTime() > DATE_LEEWAY_MS
      : false;

  return {
    ...c,
    createdAt: created ?? updated, // fallback so brand-new still shows a date
    updatedAt: updated,
    isEdited,
  };
};

 

const EditCollectionSelectorPage = () => {
  const [collections, setCollections] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const navigate = useNavigate();
 const go = (path) => () => {
    setMobileNavOpen(false);
    navigate(path);
  };
  // mobile drawer state
 const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const handleDiscover = go("/discover");
  const handleProfile = go("/profile");
  const handleContact = go("/contact");
  const openLearnMore = go("/about");
  const handleHome = go("/home-page");

  const fetchCollections = async () => {
    const identifier = getIdentifier(); // <- unified identifier (email or username)
    if (!identifier) {
      // optional: bounce to login if not signed in
      // navigate("/returning");
      setDataReady(true);
      return;
    }

    try {
      const res = await fetch(
        "https://squid-app-6s2hc.ondigitalocean.app/get-collections",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // backend accepts { email: identifier } even if identifier is a username
          body: JSON.stringify({ email: identifier }),
        }
      );

      const data = await res.json();
      if (res.ok && Array.isArray(data.collections)) {
        setCollections(data.collections.map(normalize));
      } else {
        console.error("Error fetching collections:", data?.error || "unknown error");
      }
    } catch (err) {
      console.error("Request failed:", err);
    } finally {
      // tiny delay to let CSS transitions feel nicer (kept from your code)
      setTimeout(() => setDataReady(true), 100);
    }
  };

  useEffect(() => {
    fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (collectionName) => {
    navigate("/edit-collection", { state: { collectionName } });
  };

  const handleDeleteClick = (name) => {
    setSelectedName(name);
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    setShowConfirm(false);
    const identifier = getIdentifier();
    if (!identifier || !selectedName) return;

    try {
      const res = await fetch(
        "https://squid-app-6s2hc.ondigitalocean.app/delete-collection",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: identifier, collectionName: selectedName }),
        }
      );

      const data = await res.json();
      if (res.ok && data.success) {
        setDataReady(false);
        await fetchCollections();
      } else {
        console.error("Delete failed:", data?.error || "unknown error");
      }
    } catch (error) {
      console.error("Delete request failed:", error);
    }
  };

  if (!dataReady) return null;

  return (
        <div className={`nav-shell ${mobileNavOpen ? "nav-shell--open" : ""}`}>
           <div
  className={`menu-overlay ${mobileNavOpen ? "open" : ""}`}
  onClick={() => setMobileNavOpen(false)}
/>

<aside className={`slide-menu ${mobileNavOpen ? "open" : ""}`}>
  <button
    className="menu-close"
    onClick={() => setMobileNavOpen(false)}
  >
    ×
  </button>

  <nav className="menu-nav">
    <button onClick={handleHome}>
      home
    </button>

    <button onClick={handleProfile}>
      profile
    </button>

    <div className="menu-divider" />

     <button onClick={handleDiscover}>
      browse collections
    </button>

    <button onClick={openLearnMore}>
      learn more
    </button>

     <button onClick={handleContact}>
      contact t.c.p.
    </button>
  </nav>
</aside>
        
               {!mobileNavOpen && (
          <header className="nav-burger-wrap" aria-label="page controls">
            <button
              type="button"
              className="nav-burger"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
            >
              ☰
            </button>
          </header>
        )}
      <div className="edit-collection-page">
      <main className={`edit-selector-page ${dataReady ? "ready" : ""}`}>
        {collections.length === 0 ? (
          <>
            <h1>
              ┌─ no <span className="highlight">collections</span> found
            </h1>
            <button
              className="create-new-button"
              onClick={() => navigate("/upload-collection")}
            >
              + create a new collection
            </button>
          </>
        ) : (
          <h1>
            ┌─ select a <span className="highlight">collection</span> to{" "}
            <span className="highlight">edit</span>
          </h1>
        )}

        <div className="collection-list">
          {collections.map((col, idx) => (
            <div key={idx} className="collection-card">
              <div
                className="collection-info"
                onClick={() => handleSelect(col.name)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" ? handleSelect(col.name) : null)}
              >
                <h2>{col.name}</h2>
                <p>total {col.itemCount}</p>
                <p>privacy: {col.collectionPrivate ? "🔐" : "🔓"}</p>
                <p>created: {fmt(col.createdAt)}</p>
                {col.isEdited && <p>last updated: {fmt(col.updatedAt)}</p>}
              </div>

              <button
                className="delete-button"
                onClick={() => handleDeleteClick(col.name)}
              >
                [ X ] delete collection
              </button>
            </div>
          ))}
        </div>

        {showConfirm && (
          <div className="terminal-modal-overlay" role="dialog" aria-modal="true">
            <div className="ecsp-terminal-modal">
              <p>
                are you sure you want to delete "<strong>{selectedName}</strong>"?
              </p>
              <div className="modal-actions">
                <button className="modal-button highlight" onClick={handleDelete}>
                  yes
                </button>
                <button
                  className="modal-button"
                  onClick={() => setShowConfirm(false)}
                >
                  no
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
    </div>
  );
};

export default EditCollectionSelectorPage;
